import { Action, ActionKind, evaluateCondition } from '@dynamic/ui';

import { MenuItem } from '#/client/models';
import { PageAliasesConfig } from '#/service/config/common.types';
import { ButtonVariant } from '#/service/constants';
import { InternalPageId, MenuItemDisplayType } from '#/service/constants/menu';
import { destinationRoutes } from '#/service/routes';
import { safeUrl } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

export const TOP_MENU_HEIGHT_PX = 80;

export const getNavigationTarget = (
  item: {
    action?: Action;
    id?: string;
  },
  defaultSelectedItemId: string,
  pages: PageAliasesConfig
): NavigationTarget => {
  if (!item?.action) {
    return {};
  }

  const { kind, parameters = {} } = item.action;

  switch (kind) {
    case ActionKind.NavigatePage: {
      const isHome = item.id === defaultSelectedItemId;
      const pageId =
        parameters.pageId ??
        pages[parameters.pageAlias as keyof PageAliasesConfig];
      const pageUrl = isHome || !pageId ? '/' : `/page/${pageId}`;

      return {
        internalUrl: pageUrl,
        exact: pageUrl === '/' ? true : undefined,
      };
    }
    case ActionKind.NavigateInternal:
      return {
        internalUrl:
          destinationRoutes[parameters.view as InternalPageId] ??
          `/${parameters.view?.toLowerCase()}`,
      };
    case ActionKind.NavigateExternal:
      if (!parameters.link) {
        return {};
      }

      return {
        externalUrl: safeUrl(parameters.link),
      };
    default:
      return {};
  }
};

interface NavigationTarget {
  internalUrl?: string;
  exact?: boolean;
  externalUrl?: string;
}

export const getNavigationTargetId = (item: { action: Action }) => {
  const { view, link, pageId, pageAlias } = item?.action?.parameters ?? {};

  // one of these should be available and uniquely identify the navigation target
  return view ?? link ?? pageId ?? pageAlias;
};

export const getDefaultMenuItem = (menuItems: MenuItem[]) => {
  return menuItems?.[0];
};

export const getDefaultMenuItemId = (menuItems: MenuItem[]) => {
  return getDefaultMenuItem(menuItems)?.id;
};

export const findMenuItemByView = (
  items: MenuItem[],
  view: InternalPageId
): MenuItem | undefined => {
  return items.find((item) => item.action?.parameters?.view === view);
};

export const getVisibleItems = (context: object, menuItems?: MenuItem[]) => {
  return (menuItems ?? []).filter((item) => {
    const { visibility } = item;

    return !visibility || evaluateCondition(visibility, context);
  });
};

export const getHardcodedLoginMenuItem = () => ({
  action: {
    kind: ActionKind.Login,
  },
  display: {
    buttonVariant: ButtonVariant.Primary,
    title: tb('button.login'),
    type: MenuItemDisplayType.Button,
  },
  id: 'hardcodedLoginButton',
  visibility: {
    subject: 'user.isLoggedIn',
    type: 'EQ',
    value: 'false',
  },
});

export const isLoginAction = (action: Action) =>
  action.kind === ActionKind.Login ||
  (action.kind === ActionKind.NavigateInternal &&
    action.parameters?.view === InternalPageId.Login);
